import type {
  CreativeDecisionExperimentHandoff,
  ExperimentInputPayload,
} from './experimentTypes';

const CREATIVE_HANDOFF_PARAM_NAMES = [
  'mode', 'sourceHandoffVersion', 'sourceDraftCatalog',
  'sourceProviderMutationAllowed', 'sourceReviewRequired', 'sourceFrom', 'sourceTo',
  'sourceDeployment', 'sourceProjection',
  'sourceProjectionVersion', 'sourceScope', 'sourceProvider',
  'sourceProviderAccount', 'sourceProviderResource', 'sourceCapabilitySnapshot',
  'sourceDecision', 'sourcePrimaryAction', 'sourceAsOf', 'sourceRevision',
  'sourceArtifactSnapshot', 'sourceDecisionMetric', 'sourceEvidenceRef',
  'sourceEstimand', 'sourcePrimaryMetric', 'sourceMde',
  'sourceMinimumDurationDays', 'sourceStoppingPolicy', 'sourceGuardrail',
  'sourceFixedFactor', 'sourceFactorToVary', 'sourceSurrogateStatus',
  'sourceSurrogateReason', 'sourceSurrogateEvidence', 'sourceSurrogateMetric',
] as const;
const CREATIVE_HANDOFF_PARAM_SET = new Set<string>(CREATIVE_HANDOFF_PARAM_NAMES);

/**
 * Converts a read-only Creative Decisions URL handoff into an editable,
 * unpersisted Experiment Registry draft. Server-side validation fingerprints
 * the same lineage when the operator explicitly submits the reviewed form.
 */
export function creativeDecisionDraftFromSearch(
  params: URLSearchParams,
): ExperimentInputPayload | null {
  if ([...params.keys()].some((key) => !CREATIVE_HANDOFF_PARAM_SET.has(key))) return null;
  const required = (key: string): string | null => {
    const values = params.getAll(key);
    if (values.length !== 1) return null;
    const value = values[0]?.trim();
    return safeToken(value) ? value : null;
  };
  const mode = required('mode');
  if ((mode !== 'creative-decision-experiment-draft' && mode !== 'creative-decision-next-read')
    || required('sourceHandoffVersion') !== '1'
    || required('sourceDraftCatalog') !== 'segmently-provider-neutral-v1'
    || required('sourceProviderMutationAllowed') !== 'false'
    || required('sourceReviewRequired') !== 'true') return null;
  const sourceFrom = required('sourceFrom');
  const sourceTo = required('sourceTo');
  const deploymentKey = required('sourceDeployment');
  const projectionId = required('sourceProjection');
  const projectionVersion = required('sourceProjectionVersion');
  const scopeRef = required('sourceScope');
  const provider = required('sourceProvider');
  const providerAccountId = required('sourceProviderAccount');
  const providerResourceName = required('sourceProviderResource');
  const capabilitySnapshotId = required('sourceCapabilitySnapshot');
  const decision = required('sourceDecision');
  const primaryAction = required('sourcePrimaryAction');
  const sourceAsOf = required('sourceAsOf');
  if (!sourceFrom || !sourceTo || !isDateOnly(sourceFrom)
    || !isDateOnly(sourceTo) || sourceFrom > sourceTo
    || !deploymentKey || !projectionId || !projectionVersion || !scopeRef
    || !providerAccountId || !providerResourceName || !capabilitySnapshotId
    || !primaryAction || !sourceAsOf || Number.isNaN(Date.parse(sourceAsOf))
    || !['meta', 'google_ads', 'tiktok_ads'].includes(provider ?? '')
    || !['observe', 'hold', 'investigate', 'controlled_scale', 'refresh_due', 'stop_candidate']
      .includes(decision ?? '')
    || (mode === 'creative-decision-experiment-draft' && primaryAction !== 'review_scale_probe')
    || (mode === 'creative-decision-next-read' && primaryAction !== 'review_next_read')) return null;
  const list = (key: string, limit: number): string[] => [...new Set(
    params.getAll(key).map((value) => value.trim())
      .filter((value) => safeToken(value)),
  )].slice(0, limit).sort();
  const strictList = (key: string, limit: number, requiredItems = false): string[] | null => {
    const raw = params.getAll(key);
    const normalized = list(key, limit);
    if (raw.length > limit || normalized.length !== raw.length || (requiredItems && !normalized.length)) {
      return null;
    }
    return normalized;
  };
  const sourceRevisionRefs = strictList('sourceRevision', 100, true);
  const artifactSnapshotKeys = strictList('sourceArtifactSnapshot', 25);
  const decisionMetricKeys = strictList('sourceDecisionMetric', 25);
  const evidenceRefs = strictList('sourceEvidenceRef', 100, true);
  const fixedFactors = strictList('sourceFixedFactor', 25, true);
  const factorsToVary = strictList('sourceFactorToVary', 25, true);
  const registeredGuardrails = strictList('sourceGuardrail', 25, true);
  if (!sourceRevisionRefs || !artifactSnapshotKeys || !decisionMetricKeys || !evidenceRefs
    || !fixedFactors || !factorsToVary || !registeredGuardrails) return null;
  const sourceEstimand = required('sourceEstimand');
  const sourcePrimaryMetric = required('sourcePrimaryMetric');
  const sourceStoppingPolicy = required('sourceStoppingPolicy');
  const sourceMde = optionalFiniteNumber(required('sourceMde'), 0, 1);
  const sourceMinimumDurationDays = optionalInteger(
    required('sourceMinimumDurationDays'),
    1,
    365,
  );
  if (!sourceEstimand || !sourcePrimaryMetric || !sourceStoppingPolicy
    || sourceMde === undefined || sourceMinimumDurationDays === undefined) return null;
  const surrogateStatus = required('sourceSurrogateStatus');
  if (surrogateStatus !== 'available' && surrogateStatus !== 'unavailable') return null;
  const surrogateReasonCodes = strictList('sourceSurrogateReason', 25) ?? [];
  const surrogateEvidenceRefs = strictList('sourceSurrogateEvidence', 100) ?? [];
  const surrogateMetricDefinitionRef = params.has('sourceSurrogateMetric')
    ? required('sourceSurrogateMetric')
    : null;
  if ((surrogateStatus === 'available' && (!surrogateMetricDefinitionRef || !surrogateEvidenceRefs.length))
    || (surrogateStatus === 'unavailable' && !surrogateReasonCodes.length)) return null;
  const handoff: CreativeDecisionExperimentHandoff = {
    schemaVersion: 1,
    sourceKind: 'creative_decision',
    experimentType: 'web_ab',
    handoffIntent: mode === 'creative-decision-next-read' ? 'next_read' : 'scale_probe',
    sourceFrom,
    sourceTo,
    deploymentKey,
    projectionId,
    projectionVersion,
    scopeRef,
    provider: provider as CreativeDecisionExperimentHandoff['provider'],
    providerAccountId,
    providerResourceName,
    capabilitySnapshotId,
    sourceRevisionRefs,
    ...(artifactSnapshotKeys.length ? { artifactSnapshotKeys } : {}),
    decision: decision as CreativeDecisionExperimentHandoff['decision'],
    primaryAction,
    decisionMetricKeys,
    evidenceRefs,
    fixedFactors,
    factorsToVary,
    targetEstimand: sourceEstimand,
    primaryMetric: sourcePrimaryMetric,
    guardrails: registeredGuardrails,
    minimumDetectableEffect: sourceMde,
    minimumDurationDays: sourceMinimumDurationDays,
    stoppingPolicy: sourceStoppingPolicy,
    surrogate: {
      status: surrogateStatus,
      reasonCodes: surrogateReasonCodes,
      evidenceRefs: surrogateEvidenceRefs,
      ...(surrogateMetricDefinitionRef ? { metricDefinitionRef: surrogateMetricDefinitionRef } : {}),
    },
    sourceAsOf: new Date(sourceAsOf).toISOString(),
  };
  const actionLabel = primaryAction.replaceAll('_', ' ');
  return {
    type: 'web_ab',
    name: `Creative decision: ${providerResourceName}`.slice(0, 160),
    hypothesis: {
      statement: `Test a bounded ${actionLabel} for ${providerResourceName} while holding the registered deployment and measurement scope fixed.`,
      primaryMetric: handoff.primaryMetric,
      successCriteria: creativeDecisionSuccessCriteria(handoff),
      guardrails: [...handoff.guardrails],
    },
    creativeDecisionHandoff: handoff,
  };
}

export function creativeDecisionSuccessCriteria(
  handoff: CreativeDecisionExperimentHandoff,
): string {
  return `Detect the registered MDE ${handoff.minimumDetectableEffect} after at least ${handoff.minimumDurationDays} days under ${handoff.stoppingPolicy}; require no deterioration in the registered guardrails.`;
}

function safeToken(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const normalized = value.trim();
  return Boolean(normalized)
    && normalized.length <= 512
    && !/[\u0000-\u001F\u007F]/.test(normalized)
    && !/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(normalized)
    && !/(?:bearer\s+|gs:\/\/|\/Users\/|\/private\/|-----BEGIN|(?:access|refresh|id)?token|password|secret|credential)/i.test(normalized);
}

function optionalFiniteNumber(
  value: string | null,
  exclusiveMinimum: number,
  inclusiveMaximum: number,
): number | undefined {
  if (value === null || value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > exclusiveMinimum && parsed <= inclusiveMaximum
    ? parsed
    : undefined;
}

function optionalInteger(
  value: string | null,
  inclusiveMinimum: number,
  inclusiveMaximum: number,
): number | undefined {
  if (value === null || value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= inclusiveMinimum && parsed <= inclusiveMaximum
    ? parsed
    : undefined;
}

function isDateOnly(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function clearCreativeDecisionHandoffParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  CREATIVE_HANDOFF_PARAM_NAMES.forEach((key) => next.delete(key));
  return next;
}
